import React, { useState } from 'react';
import { Dropdown, OverlayTrigger, DropdownMenu } from 'react-bootstrap';
import { BsCaretDownFill, BsGlobe } from 'react-icons/bs';
import * as BsIcons from 'react-icons/bs';
import { useSelector } from 'react-redux';

import BayTooltip from '../../../../BayTooltip';
import LaunchpadWidgetWrapper from '../../LaunchpadWidgetWrapper';
import ReactSkeleton from '../../ReactSkeleton';

import '../Widgets.scss';

interface Link {
	id: string;
	label: string;
	path: string;
	isVisible: boolean;
	key?: string;
}

interface HeaderIcon {
	name: string;
}

interface WebsiteDetail {
	websiteAddress?: string;
	title?: string;
}

interface WebsiteWidgetProps {
	widgetDetails?: any;
	editMode?: boolean;
	widgets?: any[];
	setWidgets?: (widgets: any[]) => void;
	setSnackbar?: (snackbar: any) => void;
	t: (key: string, params?: any) => string;
	defaultWidgetStyles?: any;
	widgetCustomizationData?: any;
	setWidgetCustomizationData?: (data: any) => void;
	widgetFinishStatus?: any;
	setWidgetFinishStatus?: (status: any) => void;
	isInPreviewMode?: boolean;
	editModalInputFields?: any;
	setEditModalInputFields?: (fields: any) => void;
	widgetDrawerThemeSettings?: any;
	handleLinkClick?: any;
	currentBreakpoint?: string;
	selectWebsiteDetails?: any;
	isLoading?: boolean;
	selectLaunchpadSettingsData?: any;
	selectSitesBayWebsiteDetails?: any;
	checkAndAdjustLinkPath?: (params: {
		linkPath: string;
		activeSection: string;
	}) => { linkPath: string; useSSO?: boolean };
}

const WebsiteWidget: React.FC<WebsiteWidgetProps> = ({
	widgetDetails = [],
	editMode = false,
	widgets = [],
	setWidgets = () => {},
	setSnackbar = () => {},
	t,
	defaultWidgetStyles,
	widgetCustomizationData,
	setWidgetCustomizationData = () => {},
	widgetFinishStatus,
	setWidgetFinishStatus = () => {},
	isInPreviewMode,
	editModalInputFields,
	setEditModalInputFields = () => {},
	widgetDrawerThemeSettings,
	handleLinkClick = () => {},
	currentBreakpoint,
	selectWebsiteDetails,
	isLoading,
	selectLaunchpadSettingsData,
	selectSitesBayWebsiteDetails,
	checkAndAdjustLinkPath = () => ({ linkPath: '', useSSO: false }),
}) => {
	const websiteDetails = useSelector(selectWebsiteDetails) as WebsiteDetail;
	const sitesBayWebsiteDetails = useSelector(selectSitesBayWebsiteDetails) as WebsiteDetail[];

	const [showDropdown, setShowDropdown] = useState<string | null>(null);

	const iconName =
		(isInPreviewMode
			? widgetCustomizationData?.headerIcon
			: defaultWidgetStyles?.headerIcon
		)?.name || 'BsGlobe';
	const IconComponent = (BsIcons as any)[iconName] || BsIcons.BsGlobe;

	const handleWebsiteLinkClicked = (linkPath: string) => {
		window.open(
			linkPath,
			!linkPath.startsWith('/?') && !linkPath.startsWith('/')
				? '_blank'
				: '_self',
			'noopener,noreferrer',
		);
	};

	return (
		<LaunchpadWidgetWrapper
			defaultWidgetStyles={defaultWidgetStyles}
			editModalInputFields={editModalInputFields}
			editMode={editMode}
			handleLinkClick={handleLinkClick}
			headerDescription={
				isInPreviewMode
					? widgetCustomizationData?.headerTitleDescription
					: defaultWidgetStyles?.headerTitleDescription
			}
			headerIcon={
				<IconComponent
					size={32}
					style={{
						color: isInPreviewMode
							? widgetCustomizationData?.iconColor
							: defaultWidgetStyles?.iconColor,
					}}
				/>
			}
			headerText={
				isInPreviewMode
					? widgetCustomizationData?.headerTitle
					: defaultWidgetStyles?.headerTitle
			}
			hideHeader={isLoading}
			iconColor={
				isInPreviewMode
					? widgetCustomizationData?.iconBgColor
					: defaultWidgetStyles?.iconBgColor
			}
			{...({
				isThemeSelectable: true,
				isTitleBlock: true,
			} as any)}
			selectLaunchpadSettingsData={selectLaunchpadSettingsData}
			setEditModalInputFields={setEditModalInputFields}
			setSnackbar={setSnackbar}
			setWidgetCustomizationData={setWidgetCustomizationData}
			setWidgetFinishStatus={setWidgetFinishStatus}
			setWidgets={setWidgets}
			t={t}
			widgetCustomizationData={widgetCustomizationData}
			widgetDetails={widgetDetails}
			widgetDrawerThemeSettings={widgetDrawerThemeSettings}
			widgetFinishStatus={widgetFinishStatus}
			widgets={widgets}
		>
			<div className="website-widget-body">
				{isLoading ? (
					<>
						<ReactSkeleton.HeaderLayout />
						<div className="mt-3">
							<ReactSkeleton.LinksLayout />
						</div>
					</>
				) : (
					<div className="d-flex flex-column">
						{(isInPreviewMode
							? widgetCustomizationData?.links
							: defaultWidgetStyles?.links
						)?.map((link, index) => {
							if (!link.isVisible) return null;

							if (link.id === 'viewWebsites') {
								return (
									<OverlayTrigger
										key={index}
										rootClose
										container={document.body}
										overlay={
											<DropdownMenu
												show
												className="overflow-y-auto overflow-x-hidden"
												id="website_dropdown_menu"
												style={{
													width: currentBreakpoint === 'xl' ? '230px' : '325px',
													height: '255px',
												}}
											>
												<div>
													<label className="website-label fw-bold px-3 py-2 text-uppercase">
														Website From My Profile
													</label>
													{websiteDetails?.websiteAddress ? (
														<Dropdown.Item
															className="px-3 py-2 dropdown-item-hover"
															style={{
																cursor: websiteDetails?.title
																	? 'pointer'
																	: 'default',
															}}
															onClick={() => {
																if (!websiteDetails?.title) return;
																setShowDropdown(null);
																const url =
																	websiteDetails?.websiteAddress?.startsWith(
																		'http',
																	)
																		? websiteDetails.websiteAddress
																		: `https://${websiteDetails.websiteAddress}`;
																handleWebsiteLinkClicked(url);
															}}
														>
															<div className="d-flex align-items-center">
																<div
																	className={`website-icon text-white p-1 d-flex align-items-center justify-content-center me-2 mb-0 ${websiteDetails?.title ? 'bg-primary' : 'bg-secondary'}`}
																>
																	<BsGlobe color="var(--bs-white)" size={10} />
																</div>
																<div className="d-flex flex-column">
																	{currentBreakpoint === 'xl' ? (
																		<BayTooltip
																			overlay={websiteDetails?.title || '-'}
																			placement="top"
																		>
																			<div>
																				<div
																					className="website-title fw-normal fs-6 text-dark text-truncate"
																					style={{ width: '135px' }}
																				>
																					{websiteDetails?.title || '-'}
																				</div>
																			</div>
																		</BayTooltip>
																	) : (
																		<div>
																			<div
																				className="website-title-label fw-normal fs-6 text-dark text-truncate"
																				style={{ width: '230px' }}
																			>
																				{websiteDetails?.title || '-'}
																			</div>
																		</div>
																	)}
																</div>
															</div>
														</Dropdown.Item>
													) : (
														<div className="website-no-website-text px-3 py-2 text-dark fw-normal">
															{t('noWebsite')}
														</div>
													)}
													<Dropdown.Divider className="mb-0" />
												</div>
												<div>
													<label className="website-label fw-bold px-3 py-2 text-uppercase">
														SitesBay Websites
													</label>
													{sitesBayWebsiteDetails?.length > 0 ? (
														sitesBayWebsiteDetails.map(
															(item: WebsiteDetail, itemIndex: number) =>
																item.websiteAddress && (
																	<Dropdown.Item
																		key={itemIndex}
																		className="px-3 py-2 dropdown-item-hover overflow-auto"
																		style={{
																			cursor: item?.title
																				? 'pointer'
																				: 'default',
																		}}
																		onClick={() => {
																			if (!item?.title) return;
																			setShowDropdown(null);
																			const url =
																				item.websiteAddress?.startsWith('http')
																					? item.websiteAddress
																					: `https://${item.websiteAddress}`;
																			handleWebsiteLinkClicked(url);
																		}}
																	>
																		<div className="d-flex align-items-center">
																			<div
																				className={`website-icon mb-0 text-white p-1 d-flex align-items-center justify-content-center me-2 ${item?.title ? 'bg-primary' : 'bg-secondary'}`}
																			>
																				<BsGlobe
																					color="var(--bs-white)"
																					size={10}
																				/>
																			</div>
																			<div className="d-flex flex-column">
																				{currentBreakpoint === 'xl' ? (
																					<BayTooltip
																						overlay={item?.title || '-'}
																						placement="top"
																					>
																						<div>
																							<div
																								className="website-title fw-normal fs-6 text-dark text-truncate"
																								style={{ width: '135px' }}
																							>
																								{item?.title || '-'}
																							</div>
																						</div>
																					</BayTooltip>
																				) : (
																					<div>
																						<div
																							className="website-title-label fw-normal fs-6 text-dark text-truncate"
																							style={{ width: '230px' }}
																						>
																							{item?.title || '-'}
																						</div>
																					</div>
																				)}
																			</div>
																		</div>
																	</Dropdown.Item>
																),
														)
													) : (
														<div className="website-no-website-text px-3 py-2 text-dark fw-normal">
															{t('noWebsiteConfigured')}
														</div>
													)}
												</div>
											</DropdownMenu>
										}
										placement="bottom-start"
										popperConfig={{
											modifiers: [
												{ name: 'offset', options: { offset: [6, -5] } },
											],
										}}
										show={showDropdown === 'website'}
										trigger="click"
										onToggle={(isOpen) =>
											setShowDropdown(isOpen ? 'website' : null)
										}
									>
										{({ ref, ...triggerHandler }) => (
											<div
												ref={ref}
												{...triggerHandler}
												className="d-inline-block"
												onClick={() =>
													setShowDropdown(
														showDropdown === 'website' ? null : 'website',
													)
												}
											>
												<a
													className="d-flex align-items-center text-start p-2 text-decoration-none fs-6 cursor-pointer"
													href="#"
													style={{
														color: isInPreviewMode
															? widgetCustomizationData?.linkColor
															: defaultWidgetStyles?.linkColor,
													}}
													onClick={(e) => {
														e.preventDefault();
														setShowDropdown(
															showDropdown === 'website' ? null : 'website',
														);
													}}
												>
													{link.label}
													<BsCaretDownFill
														className="ms-2"
														style={{
															color: isInPreviewMode
																? widgetCustomizationData?.linkColor
																: defaultWidgetStyles?.linkColor,
														}}
													/>
												</a>
											</div>
										)}
									</OverlayTrigger>
								);
							} else {
								const { linkPath: newLink, useSSO } = checkAndAdjustLinkPath({
									linkPath: link.path,
									activeSection: 'websites',
								});
								return (
									<a
										key={link.id}
										className="text-start p-2 border-0 text-decoration-none fs-6 d-inline-block"
										href={newLink}
										style={{
											color: isInPreviewMode
												? widgetCustomizationData?.linkColor
												: defaultWidgetStyles?.linkColor,
											width: 'fit-content',
										}}
										target="_blank"
										onClick={(e) => handleLinkClick(e, newLink, useSSO)} rel="noreferrer"
									>
										{link.label}
									</a>
								);
							}
						})}
					</div>
				)}
			</div>
		</LaunchpadWidgetWrapper>
	);
};

export default WebsiteWidget;
